import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  SquaresFour, Bed, CalendarBlank, ClipboardText, Receipt, GearSix, SignOut, ArrowsClockwise, Calculator,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, testId: "nav-dashboard" },
  { to: "/rooms", label: "Rooms", icon: Bed, testId: "nav-rooms" },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank, testId: "nav-calendar" },
  { to: "/bookings", label: "Bookings", icon: ClipboardText, testId: "nav-bookings" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices" },
  { to: "/settings", label: "Settings", icon: GearSix, testId: "nav-settings" },
];

const CURRENCIES = ["ZMW", "USD", "CNY"];

export default function Layout() {
  const { user, logout } = useAuth();
  const { currency, setCurrency, settings } = useCurrency();
  const [sync, setSync] = useState(null);
  const navigate = useNavigate();

  const hasAccounting = user?.role === "admin" || (user?.accounting_access || []).length > 0;
  const navItems = hasAccounting
    ? [...NAV.slice(0, 5), { to: "/accounting", label: "Accounting", icon: Calculator, testId: "nav-accounting" }, NAV[5]]
    : NAV;

  useEffect(() => {
    api.get("/sync/overview").then(({ data }) => setSync(data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-transparent bg-forest" data-testid="sidebar">
        <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-5">
          <img src="/logo.png" alt="EasyStay Manager" className="h-9 w-9 rounded-lg object-cover" data-testid="sidebar-logo-icon" />
          <div className="font-heading text-lg font-bold leading-none" data-testid="sidebar-logo">
            <span className="text-forest-foreground">Easy</span><span className="text-primary">Stay</span><span className="font-heading font-bold text-emerald-400">M</span>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-3 py-4">
          {navItems.map(({ to, label, icon: Icon, end, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-forest-foreground/70 hover:bg-white/10 hover:text-forest-foreground"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-2 text-xs text-forest-foreground/60" data-testid="sync-status-badge">
            <ArrowsClockwise size={14} className={sync?.connected ? "text-status-confirmed" : "text-status-pending"} />
            {sync ? `EasyStay sync: ${sync.bookings.synced}/${sync.bookings.total} bookings` : "EasyStay sync-ready"}
          </div>
        </div>
      </aside>

      <div className="ml-60 flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-background/90 px-6 py-3 backdrop-blur-xl" data-testid="topbar">
          <div>
            <div className="font-heading text-lg font-bold" data-testid="topbar-property-name">
              {settings?.property_name || "EasyStay Lodge"}
            </div>
            <div className="text-xs text-muted-foreground">{settings?.address || "Zambia"}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex overflow-hidden rounded-sm border border-border" data-testid="currency-toggle">
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  data-testid={`currency-toggle-${c.toLowerCase()}`}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    currency === c ? "bg-forest text-forest-foreground" : "bg-card text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="text-right">
              <div className="text-sm font-medium" data-testid="topbar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="rounded-sm border border-border p-2 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
              title="Log out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
